import {ReactElement, useEffect, useMemo, useState} from "react";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {SearchRounded} from "@material-ui/icons";
import {Button} from "@zendeskgarden/react-buttons";
import {
    ListPayrollJurisdictionsQuery,
    PropertyType,
    useListPayrollJurisdictionsLazyQuery
} from "../../../../__generated__/generated_types";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import SelectionBar from "../../users/components/SelectionBar";
import {JurisdictionFormModal} from "./JurisdictionFormModal";
import {JurisdictionDeleteModal} from "./JurisdictionDeleteModal";
import {JurisdictionPropertiesModal} from "./JurisdictionPropertiesModal";
import * as css from "./styles/jurisdictionStyles.module.scss";
import {AdminWrapper} from "../../AdminWrapper";
import AdminHeader from "../../AdminHeader";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";

type Jurisdiction = ListPayrollJurisdictionsQuery["listPayrollJurisdictions"][0];

const DISALLOWED_PROPERTY_TYPES = [PropertyType.Acquisition, PropertyType.Template];

export function JurisdictionList(): ReactElement {
    const [listJurisdictions, {data, loading}] = useListPayrollJurisdictionsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const {properties} = useProperties();

    const [search, setSearch] = useState<string | null>(null);
    const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
    const [createFormVisible, setCreateFormVisible] = useState<boolean>(false);
    const [editingJurisdiction, setEditingJurisdiction] = useState<Jurisdiction | null>(null);
    const [propertiesJurisdiction, setPropertiesJurisdiction] = useState<Jurisdiction | null>(null);
    const [filteredData, setFilteredData] = useState<Jurisdiction[]>([]);
    const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);

    const allowedProperties = useMemo(() => {
        if(!properties) {
            return [];
        }
        return properties.filter(p => !DISALLOWED_PROPERTY_TYPES.includes(p.propertyType))
    }, [properties]);

    const propertyNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if(allowedProperties) {
            for(const p of allowedProperties) {
                if(!DISALLOWED_PROPERTY_TYPES.includes(p.propertyType)) {
                    map.set(p.id, p.name);
                }
            }
        }
        return map;
    }, [allowedProperties]);

    useEffect(() => {
        listJurisdictions();
    }, []);

    useEffect(() => {
        if(loading) {
            return;
        }

        if(!data) {
            setFilteredData([]);
            return;
        }

        let filtered = data.listPayrollJurisdictions;
        if(search !== null && search !== "") {
            filtered = filtered.filter(j => j.name.toLowerCase().includes(search.toLowerCase()));
        }
        setFilteredData(filtered);
    }, [data, loading, search]);

    function clearSelected() {
        setSelectedJurisdictions([]);
    }

    function closeForm(saved: boolean): void {
        setCreateFormVisible(false);
        setEditingJurisdiction(null);
        if(saved) {
            listJurisdictions();
        }
    }

    function closePropertiesModal(changed: boolean): void {
        setPropertiesJurisdiction(null);
        if(changed) {
            listJurisdictions();
        }
    }

    function resolvePropertyNames(jurisdiction: Jurisdiction): string {
        return jurisdiction.payrollPropertyJurisdiction
            .map(pj => propertyNameMap.get(pj.propertyId) ?? pj.propertyId)
            .join(", ");
    }

    return <AdminWrapper>
        <AdminHeader title="Payroll Jurisdictions" subtitle="Manage your payroll jurisdictions." />
        <div className={css.jurisdictionPageInner}>
            <div className={css.tableHeaderActions}>
                <h5 className={css.tableHeaderActionsLeft}>
                    Jurisdictions
                    {
                        loading && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>
                    }
                </h5>
                <div className={css.tableHeaderActionsRight}>
                    <Field>
                        <MediaInput
                            className={css.tableHeaderActionsSearch}
                            placeholder="Search"
                            isCompact
                            onChange={evt => setSearch(evt.target.value.toLowerCase())}
                            start={
                                <SearchRounded/>
                            }
                        />
                    </Field>
                    <Button isPrimary className={css.tableHeaderActionsButton}
                            onClick={() => setCreateFormVisible(true)}>
                        Add Jurisdiction
                    </Button>
                </div>
            </div>
            <div className={css.tableWrapper}>
                <Table className={css.jurisdictionTable} size={"large"}>
                    <Head isSticky>
                        <HeaderRow className={css.headerRow}>
                            <HeaderCell isMinimum className={css.checkboxCell}/>
                            <HeaderCell style={{borderLeft: "none"}}>Name</HeaderCell>
                            <HeaderCell>Properties</HeaderCell>
                            <HeaderCell>Status</HeaderCell>
                        </HeaderRow>
                    </Head>
                    <Body>
                        {
                            filteredData.map(j => {
                                const propertyNames = resolvePropertyNames(j);
                                return <Row key={j.id} className={css.bodyRow}>
                                    <Cell className={css.checkboxCell}>
                                        <Field>
                                            <Checkbox
                                                className={css.checkboxInput}
                                                checked={selectedJurisdictions.includes(j.id)}
                                                onChange={() => {
                                                    const idx = selectedJurisdictions.indexOf(j.id);
                                                    const copy = [...selectedJurisdictions];
                                                    if(idx > -1) {
                                                        copy.splice(idx, 1);
                                                    } else {
                                                        copy.push(j.id);
                                                    }
                                                    setSelectedJurisdictions(copy);
                                                }}
                                            >
                                                <Label className={css.checkboxInput}/>
                                            </Checkbox>
                                        </Field>
                                    </Cell>
                                    <Cell style={{borderLeft: "none"}}>{j.name}</Cell>
                                    <Cell className={css.propertiesCell}>
                                        <span
                                            className={css.propertiesLink}
                                            onClick={() => setPropertiesJurisdiction(j)}
                                        >
                                            {propertyNames || "Manage Properties"}
                                        </span>
                                    </Cell>
                                    <Cell>
                                        {j.disabled
                                            ? <span className={css.disabledBadge}>Disabled</span>
                                            : <span className={css.enabledBadge}>Enabled</span>
                                        }
                                    </Cell>
                                </Row>;
                            })
                        }
                    </Body>
                </Table>
            </div>
        </div>
        <div className={css.modalsWrapper}>
            <SelectionBar
                className={css.selectionBar}
                selected={selectedJurisdictions}
                onEdit={() => {
                    if(selectedJurisdictions.length !== 1) {
                        return;
                    }
                    const id = selectedJurisdictions[0];
                    const toEdit = data?.listPayrollJurisdictions.find(j => j.id === id);
                    if(!toEdit) {
                        setEditingJurisdiction(null);
                    } else {
                        setEditingJurisdiction(toEdit);
                    }
                }}
                onDelete={() => setDeleteVisible(true)}
                name="Jurisdiction"
                namePlural="Jurisdictions"
            />
            {createFormVisible &&
                <JurisdictionFormModal closed={closeForm} existingJurisdiction={undefined}/>
            }
            {editingJurisdiction !== null &&
                <JurisdictionFormModal closed={closeForm} existingJurisdiction={editingJurisdiction}/>
            }
            {deleteVisible &&
                <JurisdictionDeleteModal
                    close={deleted => {
                        setDeleteVisible(false);
                        if(deleted) {
                            clearSelected();
                            listJurisdictions();
                        }
                    }}
                    jurisdictionIds={selectedJurisdictions}/>
            }
            {propertiesJurisdiction !== null && allowedProperties && allowedProperties.length > 0 &&
                <JurisdictionPropertiesModal
                    jurisdiction={propertiesJurisdiction}
                    allProperties={allowedProperties.map(p => ({id: p.id, name: p.name}))}
                    allJurisdictions={data?.listPayrollJurisdictions ?? []}
                    closed={closePropertiesModal}
                />
            }
        </div>
    </AdminWrapper>;
}
